import React, { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import axiosInstant from '../../api'
import { rmnCatalogCountsEndpoint } from '../../api/endpoints'
import { TabHeader } from '../../components/Tabs/TabHeader'
import { usePortalPath } from '../../hooks/usePortalPath'

// Segmented list ↔ import navigation for the Catalog page pairs (replaces
// the old "Import from crawl" header button). Rendered in ListPageHeader's
// titleExtra slot on both halves of each pair, with the active tab derived
// from the route. Paths are slug-free here and re-rooted under
// /portal/:brandSlug inside a brand-portal workspace (useNetworkScope).
//
// Filters a pair shares survive the switch: each tab names the URL param it
// uses for a shared filter, and switching translates the current value
// across (the two Products pages grew up with different param names —
// rmn_brand_id vs the generic `brand` from useCommonParams). Page-specific
// params (page, search, status, discovered_brand) are dropped on purpose.
type SharedFilter = 'brand' | 'category'

export interface CatalogTab {
  label: string
  path: string
  filterKeys?: Partial<Record<SharedFilter, string>>
  // Which figure from GET /rmn/catalog-counts this tab shows as its badge.
  count?: { pair: 'products' | 'brands'; key: 'catalog' | 'discovered' }
}

interface CatalogCounts {
  products: { catalog: number; discovered: number }
  brands: { catalog: number; discovered: number }
}

export const PRODUCT_CATALOG_TABS: CatalogTab[] = [
  {
    label: 'Catalog',
    path: '/products',
    filterKeys: { brand: 'rmn_brand_id', category: 'rmn_category_id' },
    count: { pair: 'products', key: 'catalog' },
  },
  {
    label: 'Discovered',
    path: '/products/discovered',
    filterKeys: { brand: 'brand', category: 'category' },
    count: { pair: 'products', key: 'discovered' },
  },
]

// The Discovered page's "Unmatched" sentinel — only it understands this.
const UNMATCHED = 'none'

export function carriedSearch(from: CatalogTab, to: CatalogTab, search: string): string {
  const current = new URLSearchParams(search)
  const next = new URLSearchParams()
  const keys: SharedFilter[] = ['brand', 'category']
  keys.forEach((k) => {
    const fromKey = from.filterKeys && from.filterKeys[k]
    const toKey = to.filterKeys && to.filterKeys[k]
    if (!fromKey || !toKey) return
    const value = current.get(fromKey)
    if (!value) return
    // "Unmatched" means no catalog brand — on the Catalog tab that is simply
    // no filter, so it doesn't travel there.
    if (value === UNMATCHED && toKey !== 'brand') return
    next.set(toKey, value)
  })
  const qs = next.toString()
  return qs ? `?${qs}` : ''
}

export const BRAND_CATALOG_TABS: CatalogTab[] = [
  { label: 'Catalog', path: '/catalog/brands', count: { pair: 'brands', key: 'catalog' } },
  { label: 'Discovered', path: '/catalog/brands/discovered', count: { pair: 'brands', key: 'discovered' } },
]

// Count pill next to a tab label. Grey on the inactive tab, tinted on the
// active one, so the pair reads as "97 in catalog / 599 to review" at a glance.
function CountBadge({ value, active }: { value: number; active: boolean }) {
  return (
    <span
      className={`inline-flex items-center justify-center min-w-[22px] h-[18px] px-1.5 rounded-full text-[11px] font-semibold tabular-nums ${
        active ? 'bg-tint text-accent-text' : 'bg-surface text-text-2'
      }`}
    >
      {value.toLocaleString('en-US')}
    </span>
  )
}

export function CatalogTabs({ tabs }: { tabs: CatalogTab[] }) {
  const navigate = useNavigate()
  const { pathname, search } = useLocation()
  // Counts for the badges — one cheap call per mount (i.e. per page view);
  // best-effort, the tabs work without them.
  const [counts, setCounts] = useState<CatalogCounts | null>(null)
  useEffect(() => {
    let mounted = true
    axiosInstant
      .get(rmnCatalogCountsEndpoint)
      .then((res) => {
        if (mounted) setCounts(res.data)
      })
      .catch(() => {
        if (mounted) setCounts(null)
      })
    return () => {
      mounted = false
    }
  }, [pathname])
  // usePortalPath is the one link idiom (TDC-1609); `full` is kept as a local
  // alias so the tab-matching below still reads clearly.
  const { p } = usePortalPath()
  const full = p
  const selected = Math.max(
    tabs.findIndex((t) => full(t.path) === pathname),
    0
  )
  return (
    <TabHeader
      variant="pill"
      tabs={tabs.map((t, i) => {
        const value = counts && t.count ? counts[t.count.pair][t.count.key] : null
        return {
          value: i,
          text: t.label,
          badge: value === null ? undefined : <CountBadge value={value} active={i === selected} />,
        }
      })}
      selectedTab={selected}
      setSelectedTab={(i) => {
        if (i === selected || !tabs[i]) return
        navigate(full(tabs[i].path) + carriedSearch(tabs[selected], tabs[i], search))
      }}
    />
  )
}
